// resources/js/app.ts
import { createApp, h, type DefineComponent } from 'vue'
import { createI18n } from 'vue-i18n'
import { createPinia } from 'pinia'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import axios from 'axios'
import { useNotificationStore } from '@/store/notification.store'
import { plugin as VueTippy } from 'vue-tippy'
import 'tippy.js/dist/tippy.css'
import { fetchBundle, isSupportedLocale, type Locale } from '@/i18n/bundle'
import { createInertiaApp, Link, router } from '@inertiajs/vue3'
import { InertiaProgress } from '@inertiajs/progress'
import './echo'
import '@/utils/axiosUtils'
import { configureAnalytics, initAnalyticsIfConsented, trackPageview, identifyUser, setGroup } from '@/utils/analytics'

import { watch } from 'vue'
import FeatureSurveyHost from '@/components/widgets/FeatureSurveyHost.vue'
import { FEATURE_SURVEYS_ENABLED } from '@/utils/featureSurveys'
;(window as any).__APP_TS_BOOTED__ = true
document.documentElement.setAttribute('data-app-ts', 'booted')

interface ServiceWorkerData {
  action?: string
  notification?: unknown
  [key: string]: unknown
}

declare global {
  interface Window {
    INERTIA_DEBUG?: boolean
    __SW_BUFFER__?: MessageEvent[]
    __SW_BUFFER_HANDLER__?: (ev: MessageEvent) => void
    // Superchat is now handled in a separate file (e.g. services/superchat.ts) + Header
  }
}

if (window.INERTIA_DEBUG) console.log('Inertia Debugging Enabled')

InertiaProgress.init({
  delay: 0,
  color: '#f97316',
  includeCSS: true,
  showSpinner: false
})

let pending = 0
let startTimer: number | undefined
let visibleUntil = 0

const START_DELAY = 50
const MIN_VISIBLE = 300

function showBusySoon() {
  window.clearTimeout(startTimer)
  startTimer = window.setTimeout(() => {
    document.documentElement.classList.add('is-busy')
    document.documentElement.setAttribute('aria-busy', 'true')
    visibleUntil = Date.now() + MIN_VISIBLE
  }, START_DELAY)
}

function hideBusyWhenSafe() {
  window.clearTimeout(startTimer)
  const remaining = visibleUntil - Date.now()
  const doHide = () => {
    if (pending === 0) {
      document.documentElement.classList.remove('is-busy')
      document.documentElement.removeAttribute('aria-busy')
    }
  }
  remaining > 0 ? setTimeout(doHide, remaining) : doHide()
}

router.on('start', () => {
  pending++
  if (pending === 1) showBusySoon()
})

router.on('finish', () => {
  pending = Math.max(0, pending - 1)
  if (pending === 0) hideBusyWhenSafe()
})

router.on('error', () => {
  pending = 0
  hideBusyWhenSafe()
})

router.on('invalid', () => {
  pending = 0
  hideBusyWhenSafe()
})

router.on('finish', () => trackPageview())

router.on('navigate', event => {
  const featureSettings = (event.detail.page.props as Record<string, any>).featureSettings
  configureAnalytics(featureSettings?.posthog)
  initAnalyticsIfConsented()
})

/** -------------------------------------------------
 *  TOP-LEVEL SW BUFFER LISTENER (runs before Pinia)
 *  ------------------------------------------------ */
if ('serviceWorker' in navigator && !window.__SW_BUFFER_HANDLER__) {
  window.__SW_BUFFER__ = []
  window.__SW_BUFFER_HANDLER__ = (ev: MessageEvent) => {
    window.__SW_BUFFER__!.push(ev.data)
  }
  navigator.serviceWorker.addEventListener('message', window.__SW_BUFFER_HANDLER__)
}

/** -------------------------------------------------
 *  OPTIONAL: Global SW registration (no push!)
 *  ------------------------------------------------ */
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js?v=1.1.3', { scope: '/' }).catch(err => console.warn('SW registration failed in app.ts:', err))
}


function getVueComponentName(instance: any): string {
  return instance?.type?.__name || instance?.type?.name || instance?.$options?.name || 'AnonymousComponent'
}

window.onerror = function (message, source, line, column, error) {
  console.error('[Window Error]', {
    message,
    source,
    line,
    column,
    error,
    stack: error?.stack
  })
}

window.addEventListener('unhandledrejection', event => {
  console.error('[Unhandled Promise Rejection]', {
    reason: event.reason,
    message: event.reason?.message,
    stack: event.reason?.stack
  })
})

/** ---------------------------
 *  App bootstrap
 *  --------------------------- */
const initApp = async () => {
  // prefer persisted choice (localStorage), then <body data-locale>, then 'de'
  const persisted = localStorage.getItem('preferred_lang')
  const bodyLocale = document.body.dataset.locale
  const initialLocale: Locale = isSupportedLocale(persisted)
    ? persisted
    : isSupportedLocale(bodyLocale)
      ? bodyLocale
      : 'de'

  const messages: Record<string, unknown> = {
    [initialLocale]: await fetchBundle(initialLocale)
  }

  if (initialLocale !== 'de') {
    messages.de = await fetchBundle('de')
  }

  const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    useScope: 'global',
    locale: initialLocale,
    fallbackLocale: 'de',
    messages
  })

  document.documentElement.setAttribute('lang', initialLocale)
  axios.defaults.headers.common['Accept-Language'] = initialLocale

  createInertiaApp({
    resolve: name => {
      const parts = name.split('/')
      const file = parts.pop() as string
      const dir = parts.map(s => s.toLowerCase()).join('/')
      const pages = import.meta.glob('./pages/**/*.vue')
      const path = `./pages/${dir ? dir + '/' : ''}${file}.vue`
      return resolvePageComponent(path, pages) as Promise<DefineComponent>
    },

    setup({ el, App, props }) {
      // FeatureSurveyHost sits next to the page so it survives Inertia navigations.
      const app = createApp({ render: () => [h(App, props), FEATURE_SURVEYS_ENABLED ? h(FeatureSurveyHost) : null] })

      const pinia = createPinia()
      app.use(pinia)

      app.use(i18n)

      app.use(VueTippy)
      app.component('Link', Link)

      const notificationStore = useNotificationStore(pinia)

      const authUser = (
        props as unknown as {
          initialPage?: {
            props?: {
              auth?: {
                user?: {
                  firstname?: string
                  lastname?: string
                  contact?: { id?: number; email?: string; company?: { id?: number; name?: string; company_name?: string }; company_id?: number }
                }
              }
            }
          }
        }
      )?.initialPage?.props?.auth?.user
      const isLoggedIn = Boolean(authUser)

      if (isLoggedIn) {
        notificationStore.fetchNotifications(true).catch(e => {
          console.warn('Failed to prefetch notifications:', e)
        })
      }

      const featureSettings = (props as any)?.initialPage?.props?.featureSettings
      configureAnalytics(featureSettings?.posthog)
      initAnalyticsIfConsented()
      trackPageview()
      if (authUser?.contact?.id) {
        const companyName = authUser.contact.company?.name ?? authUser.contact.company?.company_name
        identifyUser(authUser.contact.id, {
          email: authUser.contact.email ?? undefined,
          firstname: authUser.firstname ?? undefined,
          lastname: authUser.lastname ?? undefined,
          company_name: companyName ?? undefined
        })
        const companyId = authUser.contact.company?.id ?? authUser.contact.company_id
        if (companyId) setGroup('company', companyId, companyName ? { name: companyName } : undefined)
      }

      /** -------------------------------------------------
       *  REAL SW HANDLER (after Pinia/store are available)
       *  ------------------------------------------------ */
      const processSWData = async (data: ServiceWorkerData) => {
        try {
          const action = data?.action
          if (action === 'save-notification') {
            if (data?.notification) {
              console.log(data.notification)
              await notificationStore.addNotification(data.notification)
            } else {
              console.warn('save-notification called without a valid notification', data)
            }
            return
          }

          if (action === 'remove-notification') {
            if (data?.idempotencyKey && typeof data.idempotencyKey === 'string') {
              await notificationStore.removeNotification(data.idempotencyKey)
            } else {
              console.warn('remove-notification called without a valid url', data)
            }
            return
          }

          if (data?.type === 'PLAY_SOUND') {
            const audio = new Audio('/sounds/monteur_notification_industrial.wav')
            audio.play().catch(err => console.warn('Unable to play sound:', err))
            return
          }

          if (data?.type === 'ENQUIRIES_UPDATED') {
            // intentionally ignored:
            // realtime layer already refreshes enquiry data
            return
          }

          console.warn('Unknown SW action:', action, data)
        } catch (e) {
          console.error('processSWData error:', e)
          if (import.meta.env.DEV) {
            setTimeout(() => {
              throw e
            })
          }
        }
      }

      if ('serviceWorker' in navigator) {
        // 1) Remove the early buffer listener
        if (window.__SW_BUFFER_HANDLER__) {
          navigator.serviceWorker.removeEventListener('message', window.__SW_BUFFER_HANDLER__)
          window.__SW_BUFFER_HANDLER__ = undefined
        }

        // 2) Attach the live handler
        const liveHandler = (ev: MessageEvent) => {
          void processSWData((ev.data || {}) as ServiceWorkerData)
        }
        navigator.serviceWorker.addEventListener('message', liveHandler)

        // 3) Flush any buffered messages
        void (async () => {
          const buffered = window.__SW_BUFFER__ ?? []
          for (const ev of buffered) {
            await processSWData((ev.data || {}) as ServiceWorkerData)
          }
          window.__SW_BUFFER__ = []
        })()
      }

      app.config.errorHandler = (err, instance, info) => {
        const error = err instanceof Error ? err : new Error(String(err))

        console.error('[Vue Error]', {
          component: getVueComponentName(instance),
          info,
          message: error.message,
          stack: error.stack,
          error,
          instance
        })
      }

      if (import.meta.env.DEV) {
        app.config.warnHandler = (msg, instance, trace) => {
          console.warn('[Vue Warn]', {
            component: getVueComponentName(instance),
            msg,
            trace,
            instance
          })
        }
      }

      app.directive('click-outside', {
        beforeMount(el: HTMLElement, binding) {
          const handler = (e: MouseEvent) => {
            const t = e.target as Node | null
            if (!t) return
            if (!(el === t || el.contains(t))) {
              binding.value?.(e)
            }
          }
          ;(el as any).__clickOutside__ = handler
          document.addEventListener('click', handler)
        },
        unmounted(el: HTMLElement) {
          const handler = (el as any).__clickOutside__
          if (handler) document.removeEventListener('click', handler)
        }
      })

      app.mount(el)
    }
  })
}

initApp().catch(error => {
  console.error('Error initializing app:', error)

  if (import.meta.env.DEV) {
    alert(
      `APP INIT ERROR:\n\n` +
      `${error?.message || error}\n\n` +
      `${error?.stack || 'No stack trace'}`
    )
  }
})
